import { Eye, Store, MapPin, Globe, Users, Target } from 'lucide-react';

export default function OverviewTab({ store, validationResults, domainConfig }) {
    return (
        <div className="p-8 space-y-8">
            {/* Quick Stats */}
            <div className="bg-gradient-to-r from-slate-50 to-slate-100/80 rounded-2xl p-6 border border-slate-200/60">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-200/60">
                        <Eye size={24} className="text-slate-700" />
                    </div>
                    Overview Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-2xl p-6 text-center border border-slate-200/60 shadow-sm">
                        <div className="text-4xl font-bold text-slate-900 mb-2">
                            {store.pages.length}
                        </div>
                        <div className="text-sm font-medium text-slate-600">Total Pages</div>
                    </div>
                    {validationResults && (
                        <>
                            <div className="bg-white rounded-2xl p-6 text-center border border-emerald-200/60 shadow-sm">
                                <div className="text-4xl font-bold text-emerald-600 mb-2">
                                    {validationResults.scrapingResults?.summary?.successful || 0}
                                </div>
                                <div className="text-sm font-medium text-slate-600">Successfully Validated</div>
                            </div>
                            <div className="bg-white rounded-2xl p-6 text-center border border-red-200/60 shadow-sm">
                                <div className="text-4xl font-bold text-red-600 mb-2">
                                    {validationResults.issues?.length || 0}
                                </div>
                                <div className="text-sm font-medium text-slate-600">Issues Found</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Store Information Summary */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                        <Store size={24} className="text-blue-600" />
                    </div>
                    Store Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                        <div className="flex items-center gap-3 mb-2">
                            <MapPin size={16} className="text-slate-600" />
                            <span className="font-bold text-slate-700">Region</span>
                        </div>
                        <div className="text-slate-900 font-medium">{store.region}</div>
                    </div>
                    {store.address && (
                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <MapPin size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Address</span>
                            </div>
                            <div className="text-slate-900 font-medium">{store.address}</div>
                        </div>
                    )}
                    {store.website && (
                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <Globe size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Website</span>
                            </div>
                            <a href={store.website} target="_blank" rel="noreferrer"
                                className="text-orange-600 hover:text-orange-700 hover:underline font-medium transition-colors">
                                {store.website}
                            </a>
                        </div>
                    )}
                    {store.contact?.email && (
                        <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/60">
                            <div className="flex items-center gap-3 mb-2">
                                <Users size={16} className="text-slate-600" />
                                <span className="font-bold text-slate-700">Email</span>
                            </div>
                            <a href={`mailto:${store.contact.email}`}
                                className="text-orange-600 hover:text-orange-700 hover:underline font-medium transition-colors">
                                {store.contact.email}
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {/* Domain Overview */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/60 shadow-sm">
                <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-purple-50 rounded-xl border border-purple-200">
                        <Target size={24} className="text-purple-600" />
                    </div>
                    Page Domains
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Object.entries(store.pages.reduce((acc, page) => {
                        if (!acc[page.domain]) acc[page.domain] = [];
                        acc[page.domain].push(page);
                        return acc;
                    }, {})).map(([domain, pages]) => {
                        const config = domainConfig[domain];
                        return (
                            <div key={domain} className={`p-6 rounded-2xl border-2 shadow-sm ${config.bgColor} ${config.borderColor}`}>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`p-2 bg-white rounded-xl border border-slate-200/60 ${config.color}`}>
                                        {config.icon}
                                    </div>
                                    <span className={`font-bold text-lg ${config.color}`}>
                                        {domain}
                                    </span>
                                </div>
                                <div className="bg-white/80 rounded-xl p-3 border border-slate-200/60">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-slate-900">
                                            {pages.length}
                                        </div>
                                        <div className="text-sm font-medium text-slate-600">
                                            page{pages.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
